import { computed, inject } from '@angular/core';
import { SortOrder } from '@map/enums';
import { CreateZoneDto, Occupancy, UpdateZoneDto, Zone } from '@map/models';
import { ZoneService } from '@map/services/zone.service';
import { tapResponse } from '@ngrx/operators';
import { signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { addEntity, removeEntity, updateEntity, upsertEntities, withEntities } from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { concatMap, defer, finalize, forkJoin, map, Observable, of, pipe, ReplaySubject, shareReplay, switchMap, take } from 'rxjs';
import { updateState, withDevtools, withDevToolsStub } from '@angular-architects/ngrx-toolkit';
import { environment } from 'src/environments/environment';
import { ReportZoneData } from 'src/app/report/models';

type ZoneState = {
    selected: Zone | null;
    occupancyList: Occupancy[] | null;
    filter: {
        query: string | null;
        order: SortOrder;
    };
    loading: boolean;
    loaded: boolean;
};

const initialState: ZoneState = {
    selected: null,
    occupancyList: null,
    filter: { query: '', order: SortOrder.Ascending },
    loading: false,
    loaded: false,
};

export const withTreeShakableDevTools = environment.production ? withDevToolsStub : withDevtools;

export interface ZoneMembershipUpdate {
    zoneId: number;
    addIds?: number[];
    removeIds?: number[];
}

export interface ZoneSaveCallbacks {
    onSuccess?: (zone: Zone) => void;
    onError?: (error: unknown) => void;
}

export type ZoneCreateRequest = Pick<Zone, 'zoneName'> &
    Partial<Pick<Zone, 'zoneEnabled' | 'sourceId' | 'sourceType' | 'metadata' | 'zoneRegions'>> &
    ZoneSaveCallbacks;

export const ZoneStore = signalStore(
    { providedIn: 'root' },
    withTreeShakableDevTools('zones'),
    withEntities<Zone>(),
    withState<ZoneState>(initialState),
    withComputed(({ entities, filter }) => ({
        zoneCount: computed(() => entities().length),
        searchResults: computed(() => {
            return entities()
                .filter((zone) => (zone.zoneName ?? '').toLowerCase().includes(filter.query()?.toLowerCase() ?? ''))
                .sort((a, b) =>
                    filter.order() === SortOrder.Ascending ? a.zoneName.localeCompare(b.zoneName) : b.zoneName.localeCompare(a.zoneName)
                );
        }),
        enabledZones: computed(() => entities().filter((zone) => zone.zoneEnabled)),
    })),
    withMethods((store, zoneService = inject(ZoneService)) => {
        let selectionVersion = 0;
        let membershipTail: Observable<void> = of(undefined);
        const mutationRevisions = new Map<number, number>();

        const revisionFor = (id: number): number => {
            if (!mutationRevisions.has(id)) mutationRevisions.set(id, 0);
            return mutationRevisions.get(id)!;
        };

        const invalidateRead = (id: number): void => {
            mutationRevisions.set(id, revisionFor(id) + 1);
        };

        const readZone = (id: number): Observable<Zone> =>
            defer(() => {
                const requestedRevision = revisionFor(id);
                return zoneService.get<Zone>(id).pipe(
                    switchMap((zone) => {
                        if (revisionFor(id) === requestedRevision) return of(zone);

                        // A Zone mutation committed while this GET was in
                        // flight. Return the successful cached version to the caller as
                        // well as the UI; refetch when the record is no longer cached.
                        const current = store.entityMap()[id];
                        return current ? of(current) : readZone(id);
                    })
                );
            });

        const updateMembership = ({ zoneId, addIds = [], removeIds = [] }: ZoneMembershipUpdate): Observable<Zone> => {
            const additions = [...addIds];
            const removals = new Set(removeIds);

            // Enqueue on subscription. Once started, keep the command alive even if its
            // initiating view closes: the server may already have committed the write.
            return defer(() => {
                const previous = membershipTail;
                const completed = new ReplaySubject<void>(1);
                membershipTail = completed.asObservable();

                return previous.pipe(
                    take(1),
                    concatMap(() => {
                        updateState(store, '[Zone] Update Membership', { loading: true });
                        return readZone(zoneId).pipe(
                            concatMap((current) => {
                                const zone: Zone = {
                                    ...current,
                                    zoneRegions: [...new Set([...current.zoneRegions, ...additions])].filter((id) => !removals.has(id)),
                                };
                                return zoneService.update<void>(zoneId, new UpdateZoneDto(zone)).pipe(map(() => zone));
                            }),
                            map((zone) => {
                                invalidateRead(zone.id);
                                updateState(store, '[Zone] Update Membership Success', upsertEntities([zone]), {
                                    selected: store.selected()?.id === zone.id ? zone : store.selected(),
                                });
                                return zone;
                            }),
                            finalize(() => updateState(store, '[Zone] Update Membership Finished', { loading: false }))
                        );
                    }),
                    finalize(() => {
                        completed.next();
                        completed.complete();
                    })
                );
            }).pipe(shareReplay({ bufferSize: 1, refCount: false }));
        };

        return {
            loadWithResult: (id: number): Observable<Zone> =>
                defer(() => {
                    const requestVersion = selectionVersion;
                    updateState(store, '[Zone] Load', { loading: true });
                    return readZone(id).pipe(
                        map((zone) => {
                            updateState(store, '[Zone] Load Success', upsertEntities([zone]), {
                                selected: selectionVersion === requestVersion && store.selected()?.id === id ? zone : store.selected(),
                            });
                            return zone;
                        }),
                        finalize(() => updateState(store, '[Zone] Load Finished', { loading: false }))
                    );
                }),
            updateMembership,
            updateFilter: (filter: { query?: string; order?: SortOrder }) => {
                updateState(store, '[Zone] Update Filter', {
                    filter: {
                        ...store.filter(),
                        query: filter.query ?? '',
                        order: filter.order ?? SortOrder.Ascending,
                    },
                });
            },
            resetFilter: () => updateState(store, '[Zone] Reset Filter', { filter: initialState.filter }),
            getAll: rxMethod<void>(
                pipe(
                    switchMap(() => {
                        if (store.loaded()) {
                            return of(store.entities());
                        }
                        updateState(store, '[Zone] Get All', { loading: true });
                        return zoneService.getAll<Zone[]>().pipe(
                            tapResponse({
                                next: (zones) => {
                                    updateState(store, '[Zone] Get All Success', upsertEntities(zones), {
                                        loaded: true,
                                        loading: false,
                                    });
                                },
                                error: (error) => {
                                    console.error('Failed to load zones', error);
                                    updateState(store, '[Zone] Get All Failure', { loading: false });
                                },
                            })
                        );
                    })
                )
            ),
            get: rxMethod<number>(
                pipe(
                    switchMap((id) => {
                        const requestVersion = ++selectionVersion;
                        updateState(store, '[Zone] Get', { loading: true });
                        return readZone(id).pipe(
                            tapResponse({
                                next: (zone) => {
                                    updateState(store, '[Zone] Get Success', upsertEntities([zone]), {
                                        selected: selectionVersion === requestVersion ? zone : store.selected(),
                                        loading: false,
                                    });
                                },
                                error: (error) => {
                                    console.error('Failed to load zone', error);
                                    updateState(store, '[Zone] Get Failure', { loading: false });
                                },
                            })
                        );
                    })
                )
            ),
            select: rxMethod<{ id: number; loadFromServer?: boolean }>(
                pipe(
                    switchMap(({ id, loadFromServer }) => {
                        const requestVersion = ++selectionVersion;
                        if (loadFromServer) {
                            updateState(store, '[Zone] Select From Server', { loading: true });
                            return readZone(id).pipe(
                                tapResponse({
                                    next: (zone) => {
                                        updateState(store, '[Zone] Select Success', upsertEntities([zone]), {
                                            selected: selectionVersion === requestVersion ? zone : store.selected(),
                                            loading: false,
                                        });
                                    },
                                    error: (error) => {
                                        console.error('Failed to select zone', error);
                                        updateState(store, '[Zone] Select Failure', { loading: false });
                                    },
                                })
                            );
                        }
                        const zone = store.entityMap()[id] ?? null;
                        updateState(store, '[Zone] Select From Cache', { selected: zone });
                        return of(zone);
                    })
                )
            ),
            deselect: () => {
                selectionVersion++;
                updateState(store, '[Zone] Deselect', { selected: null });
            },
            create: rxMethod<ZoneCreateRequest>(
                pipe(
                    switchMap(({ onSuccess, onError, ...zone }) => {
                        updateState(store, '[Zone] Create', { loading: true });
                        const createZoneDto = new CreateZoneDto(zone);
                        return zoneService.create<any>(createZoneDto).pipe(
                            tapResponse({
                                next: (response) => {
                                    const newZone = new Zone(response);
                                    invalidateRead(newZone.id);
                                    updateState(store, '[Zone] Create Success', addEntity(newZone), {
                                        selected: newZone,
                                        loading: false,
                                    });
                                    onSuccess?.(newZone);
                                },
                                error: (error) => {
                                    console.error('Failed to create zone', error);
                                    updateState(store, '[Zone] Create Failure', { loading: false });
                                    onError?.(error);
                                },
                            })
                        );
                    })
                )
            ),
            update: rxMethod<Zone & ZoneSaveCallbacks>(
                pipe(
                    switchMap(({ onSuccess, onError, ...zone }) => {
                        updateState(store, '[Zone] Update', { loading: true });
                        const zoneUpdate = new UpdateZoneDto(zone);
                        return zoneService.update<Zone>(zoneUpdate.zoneId, zoneUpdate).pipe(
                            tapResponse({
                                next: () => {
                                    invalidateRead(zone.id);
                                    updateState(
                                        store,
                                        '[Zone] Update Success',
                                        updateEntity({
                                            id: zoneUpdate.zoneId,
                                            changes: () => zone,
                                        }),
                                        {
                                            selected: store.selected()?.id === zone.id ? zone : store.selected(),
                                            loading: false,
                                        }
                                    );
                                    onSuccess?.(zone);
                                },
                                error: (error) => {
                                    console.error('Failed to update zone', error);
                                    updateState(store, '[Zone] Update Failure', { loading: false });
                                    onError?.(error);
                                },
                            })
                        );
                    })
                )
            ),
            delete: rxMethod<number>(
                pipe(
                    switchMap((id) => {
                        updateState(store, '[Zone] Delete', { loading: true });
                        return zoneService.delete(id).pipe(
                            tapResponse({
                                next: () => {
                                    invalidateRead(id);
                                    updateState(store, '[Zone] Delete Success', removeEntity(id), {
                                        selected: store.selected()?.id === id ? null : store.selected(),
                                        loading: false,
                                    });
                                },
                                error: (error) => {
                                    console.error('Failed to delete zone', error);
                                    updateState(store, '[Zone] Delete Failure', { loading: false });
                                },
                            })
                        );
                    })
                )
            ),
            getOccupancyFromReportData: rxMethod<{ zoneData: ReportZoneData[] }>(
                pipe(
                    switchMap(({ zoneData }) => {
                        if (!zoneData || zoneData.length === 0) return of([]);
                        updateState(store, '[Zone] Get Occupancy From Report Data', { loading: true });
                        const requests = zoneData.map((data) =>
                            zoneService.getOccupancyRange({
                                id: data.zoneId,
                                from: data.dateRange[0],
                                to: data.dateRange[1],
                                daily: false,
                            })
                        );

                        return forkJoin(requests).pipe(
                            tapResponse({
                                next: (results) => {
                                    const allOccupancy = results.flat();
                                    updateState(store, '[Zone] Get Occupancy From Report Data Success', {
                                        occupancyList: allOccupancy,
                                        loading: false,
                                    });
                                },
                                error: (error) => {
                                    console.error('Failed to get occupancy from report data', error);
                                    updateState(store, '[Zone] Get Occupancy From Report Data Failure', { loading: false });
                                },
                            })
                        );
                    })
                )
            ),
        };
    })
);
